import { AuthClient, isAuthRetryableFetchError } from '@supabase/auth-js';
import { withDeadline, RequestTimeout } from '../network/deadline';
import { WebConfig } from '../config';

export interface Session { user: { id: string; email?: string }; access_token: string; offlineOnly?: boolean; passwordRecovery?: boolean }
/**
 * Create the configured Supabase or demo authentication API and register session
 * change listeners. Published authenticated sessions cache an identity for local
 * access; password recovery links can clear a previous local sign-out marker.
 */
export function createAuth(config: WebConfig) {
  const identityKey = 'livestock-offline-identity:' + config.apiUrl + ':' + (config.supabaseUrl || 'demo');
  const signedOutKey = identityKey + ':signed-out';
  // Dashboard emails can return to the site root. Capture their intent before
  // the SDK consumes the fragment; signing in is only the first reset step.
  const callback = new URLSearchParams(window.location.hash.slice(1));
  const passwordLink = callback.has('access_token') && ['recovery', 'invite'].includes(callback.get('type') || '');
  let passwordRecovery = passwordLink;
  if (passwordLink || (window.location.pathname === '/account/password' && /access_token=|code=/.test(window.location.hash + window.location.search))) localStorage.removeItem(signedOutKey);
  // This identity hint reopens cached work; it is not a credential or proof of
  // current farm access. scopedFetch requires a real session before sending work.
  const offlineIdentity = (): Session | null => {
    try { const user = JSON.parse(localStorage.getItem(identityKey) || 'null'); return user?.id ? { user, access_token: '', offlineOnly: true } : null; } catch { return null; }
  };
  const key = 'livestock-demo-session:' + config.apiUrl;
  const supabase = config.authMode === 'supabase' ? new AuthClient({
    url: config.supabaseUrl + '/auth/v1',
    headers: { apikey: config.supabaseKey || '' },
    storageKey: 'livestock-auth:' + config.supabaseUrl,
    persistSession: true, autoRefreshToken: true, detectSessionInUrl: true,
  }) : undefined;
  let session: Session | null = null;
  let authGeneration = 0;
  const listeners = new Set<(value: Session | null) => void>();
  const publish = (value: Session | null) => {
    if (value && localStorage.getItem(signedOutKey)) return;
    if (value && !value.offlineOnly) localStorage.setItem(identityKey, JSON.stringify(value.user));
    session = value && passwordRecovery ? { ...value, passwordRecovery: true } : value;
    listeners.forEach(fn => fn(session));
  };
  if (!supabase) window.addEventListener('storage', event => {
    if (event.key === key) {
      try { publish(event.newValue ? JSON.parse(event.newValue) : null); } catch { publish(null); }
    }
  });
  supabase?.onAuthStateChange((event, value) => {
    // INITIAL_SESSION can be null when refresh transport fails. restore() owns
    // that decision and can retain cached work without declaring a sign-out.
    if (event === 'INITIAL_SESSION' && !value) return;
    if (event !== 'INITIAL_SESSION') authGeneration++;
    if (event === 'SIGNED_OUT') { localStorage.removeItem(identityKey); publish(null); return; }
    if (event === 'PASSWORD_RECOVERY' && value) passwordRecovery = true;
    publish(value || (!navigator.onLine ? offlineIdentity() : null));
  });
  return {
    subscribe(fn: (value: Session | null) => void) { listeners.add(fn); return () => { listeners.delete(fn); }; },
    /**
     * Restore and publish a saved session, returning the current session or null.
     * Supabase restoration uses a cached identity with an empty token and
     * `offlineOnly` when offline, or after a retryable fetch error or a 20-second
     * deadline, provided local sign-out or newer authentication has not intervened.
     * A pending refresh can still publish its result after the deadline unless
     * superseded. Non-retryable errors returned by the SDK clear the cached identity
     * and publish null. Missing or malformed demo session JSON publishes null.
     *
     * @throws SDK errors or RequestTimeout when no eligible cached fallback exists;
     * storage access and subscriber errors can also propagate.
     */
    async restore() {
      const restoreGeneration = ++authGeneration;
      if (localStorage.getItem(signedOutKey)) { publish(null); return null; }
      if (supabase) {
        const offline = offlineIdentity();
        if (!navigator.onLine && offline) { publish(offline); return offline; }
        // A suspended iPad may need to refresh an expired token on reopening.
        // Bound the wait without discarding credentials or the saved farm.
        const restoring = supabase.getSession().then(({data, error}) => {
          if (restoreGeneration !== authGeneration) return session;
          if (error) {
            if (!isAuthRetryableFetchError(error)) { localStorage.removeItem(identityKey); publish(null); }
            throw error;
          }
          publish(data.session);
          return session;
        });
        // Promise.race in withDeadline observes late rejections even after timeout.
        try { await withDeadline(() => restoring); }
        catch (error) {
          if (restoreGeneration !== authGeneration) throw error;
          // Only transport/timeout failures allow local access. Re-read the hint
          // so an explicit sign-out during restoration cannot resurrect it.
          const cached = offlineIdentity();
          if ((error instanceof RequestTimeout || isAuthRetryableFetchError(error)) && cached && !localStorage.getItem(signedOutKey)) { publish(cached); return session; }
          throw error;
        }
      } else {
        const raw = localStorage.getItem(key);
        try { publish(raw ? JSON.parse(raw) : null); } catch { publish(null); }
      }
      return session;
    },
    // Delivery asks for the current token; queued mutations never embed credentials.
    async current() {
      if (localStorage.getItem(signedOutKey)) return null;
      if (supabase) {
        const { data, error } = await supabase.getSession();
        if (error) throw error;
        session = data.session;
      }
      return session;
    },
    /**
     * Sign in, clear local sign-out, and publish the session, superseding pending
     * restores. Demo sessions are also persisted in local storage.
     * @throws Supabase sign-in errors, or an error for a non-OK demo response.
     * Fetch, JSON parsing, storage, and subscriber errors also propagate.
     */
    async login(email: string, password: string) {
      if (supabase) {
        const { data, error } = await supabase.signInWithPassword({ email, password });
        if (error) throw error;
        authGeneration++;
        localStorage.removeItem(signedOutKey);
        publish(data.session);
      } else {
        const response = await fetch(new URL('/auth/demo-login', config.apiUrl), {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }),
        });
        if (!response.ok) throw new Error('Check your demo email and password.');
        const value: Session = await response.json();
        authGeneration++;
        localStorage.setItem(key, JSON.stringify(value));
        localStorage.removeItem(signedOutKey);
        publish(value);
      }
    },
    /**
     * End local access and invalidate pending restores while retaining journals.
     * Clear the cached identity and notify subscribers before SDK sign-out.
     * @throws SDK sign-out errors even after local access has ended; storage and
     * subscriber errors also propagate and can interrupt sign-out.
     */
    async logout() {
      authGeneration++;
      // Local sign-out works offline; journals are deliberately retained.
      // Set this before SDK sign-out: a failed network call or later auth callback
      // must not restore a session the user explicitly signed out of.
      localStorage.setItem(signedOutKey, '1');
      passwordRecovery = false;
      localStorage.removeItem(identityKey);
      publish(null);
      if (supabase) { const { error } = await supabase.signOut({ scope: 'local' }); if (error) throw error; }
      else localStorage.removeItem(key);
    },
    async setPassword(password: string, nonce?: string) {
      if (!supabase) throw new Error('Demo passwords are fixed.');
      const { error } = await supabase.updateUser({ password, ...(nonce ? { nonce } : {}) });
      if (error) throw error;
      passwordRecovery = false;
      if (session) { const { passwordRecovery: _recovery, ...updated } = session; publish(updated); }
    },
    async verifyPassword(password: string) {
      if (!supabase) throw new Error('Demo credentials are fixed.');
      const { data: current, error: sessionError } = await supabase.getSession();
      if (sessionError || !current.session?.user.email) throw new Error('Sign in again to update your account.');
      const { error } = await supabase.signInWithPassword({ email: current.session.user.email, password });
      if (error) throw new Error('Check your current password and try again.');
    },
    async setEmail(email: string) {
      if (!supabase) throw new Error('Demo email addresses are fixed.');
      const { error } = await supabase.updateUser({ email: email.trim() }, { emailRedirectTo: window.location.origin + '/settings/account' });
      if (error) throw error;
    },
    async reauthenticate() {
      if (!supabase) throw new Error('Demo credentials are fixed.');
      const { error } = await supabase.reauthenticate();
      if (error) throw error;
    },
    async resetPassword(email: string) {
      if (!supabase) throw new Error('Use the documented demo password.');
      const { error } = await supabase.resetPasswordForEmail(email, { redirectTo: window.location.origin + '/account/password' });
      if (error) throw error;
    },
  };
}
export type Auth = ReturnType<typeof createAuth>;
